import { http, publicHttp } from 'core/utils/httpClient';
import type { CreateOrderImageRequest } from './dtoModels/order/createOrderImageRequest';
import type { OrderImageResponse } from './dtoModels/order/orderImageResponse';
import type { UploadOrderImageResponse } from './dtoModels/order/uploadOrderImageResponse';
import type { AxiosProgressEvent, AxiosResponse } from 'axios';
import type { OrderImage } from 'models/order/orderImage';

export const mapOrderImage = (source: OrderImageResponse): OrderImage => ({
  ...source,
  date: new Date(source.dateCreate),
  thumbnailUrl: source.previewUrl,
});

export const mapOrderImages = (source: OrderImageResponse[]): OrderImage[] =>
  source.map((s) => mapOrderImage(s));

export async function getOrderImagesAsync(orderId: number): Promise<OrderImage[]> {
  const { data } = await http.get<OrderImageResponse[]>(`orders/${orderId}/images`);

  return mapOrderImages(data);
}

export async function uploadOrderImageAsync(
  orderId: number,
  file: File,
  options?: {
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    signal?: AbortSignal;
  }
): Promise<string> {
  const { data } = await http.post<
    UploadOrderImageResponse,
    AxiosResponse<UploadOrderImageResponse>,
    CreateOrderImageRequest
  >(`orders/${orderId}/images`, {
    name: file.name,
    imageSize: file.size,
  });

  try {
    await publicHttp.put(data.uploadUrl, file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      ...options,
    });
  } catch {
    await deleteOrderImagesAsync(orderId, [data.id]);
  }

  return data.name;
}

export async function deleteOrderImagesAsync(
  orderId: number,
  orderImageIds: number[]
): Promise<void> {
  await http.delete(`orders/${orderId}/images`, { data: orderImageIds });
}
